import { Button, Col, Modal, Row, Table } from 'antd';
import React, { useEffect, useState } from 'react'
import useSWR from 'swr';
import { GET } from '../../../utils/requestActionConstants';
import { API_ROUTE } from '../../../utils/commonConstants';
import { useDispatch, useSelector } from 'react-redux';
import { setInventoriesList, setInventory, updateInventory } from '../../../store/slices/inventorySlice';
import { fetcher } from '../../../helpers/processRequest';
import AddInventory from './AddInventory';
import { EditFilled } from '@ant-design/icons';
import { Document, Image, Page, PDFViewer, StyleSheet, Text, View } from '@react-pdf/renderer';
import Barcode from 'react-barcode';
import { getInventoryBarCode } from '../../../utils/formatters';
import JsBarcode from 'jsbarcode';

const styles = StyleSheet.create({
  page: {
    padding: 20,
    flexDirection: 'column',
  },
  header: {
    textAlign: 'center',
    marginBottom: 20,
  },
  barcodeList: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap', // Allows wrapping to the next row
    gap: 10, // Space between items
    justifyContent: 'center', // Center items horizontally
  },
  barcodeItem: {
    width: 100, // Width of each barcode container
    alignItems: 'center',
    marginBottom: 10,
  },
  barcodeImage: {
    width: 80, // Adjust the barcode width
    height: 40, // Adjust the barcode height
  },
  barcodeLabel: {
    fontSize: 8,
    textAlign: 'center',
  },
});

function ProductInventory({ tabProps }) {
  const product = useSelector(state => state.productSlice.product);
  const dataFetcher = useSWR({ method: GET, url: API_ROUTE + '/inventories?productId=' + (product.id ? product.id : 0) }, fetcher);
  const inventories = useSelector(state => state.inventorySlice);
  const dispatch = useDispatch();
  const [showAddInventoryForm, setShowInventoryForm] = useState(false);
  const [showPrintablePDF, setShowPrintablePDF] = useState(false);
  const [barcodes, setBarcodes] = useState([]);

  useEffect(() => {
    if (dataFetcher.data?.data) {
      dispatch(setInventoriesList(dataFetcher.data.data));
    }
  }, [dataFetcher.data?.data])

  const updateStock = (record, value) => {
    const updateData = {
      stock: record.stock + value
    };
    dispatch(updateInventory({ inventoryId: record.id, inventoryData: updateData }));
  }

  const editStock = (record) => {
    dispatch(setInventory(record));
    setShowInventoryForm(true);
  }

  const printBarCodes = () => {
    const codes = inventories.inventoryList.map(inventory => {
      return ({
        barcode: generateBarcode(getInventoryBarCode(inventory.id)),
        count: inventory.stock > 100 ? 55 : inventory.stock,
        description: `${product.name} ${inventory.price}`
      })
    });

    setBarcodes(codes);
    setShowPrintablePDF(true)
  }

  const columns = [
    {
      title: 'Inventory Code',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
    },
    {
      title: 'Cost Price',
      dataIndex: 'costPrice',
      key: 'costPrice',
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
    },
    {
      title: 'Stock',
      dataIndex: 'stock',
      key: 'stock',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 160,
      render: (_, record) => (
        <>
          <div>
            <Button onClick={() => { updateStock(record, -1) }}>-</Button>
            <Button onClick={() => { updateStock(record, +1) }}>+</Button>
            <Button onClick={() => { editStock(record) }}><EditFilled /></Button>
          </div>
        </>
      )
    }
  ];

  const createnNewInventory = () => {
    const sku = `${product.id}-${Date.now()}`;
    const inventory = {
      barcode: sku,
      batchNumber: sku,
      stock: 0
    }

    dispatch(setInventory(inventory));

    setShowInventoryForm(true);
  }

  const closeInventoryForm = () => {
    setShowInventoryForm(false);
  }

  const generateBarcode = (value) => {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, value, { format: 'CODE128' });
    return canvas.toDataURL('image/png');
  };

  return (
    <Row gutter={[16, 16]}>
      <Modal
        centered
        maskClosable={false}
        open={showPrintablePDF}
        onOk={() => setShowPrintablePDF(false)}
        onCancel={() => setShowPrintablePDF(false)}
        styles={{ body: { maxHeight: "600px", overflowY: "auto", paddingRight: 5 } }}
      >
        <PDFViewer width="100%" height="600">
          <Document>
            <Page size="A4" style={styles.page}>
              {/* Header */}
              <View style={styles.header}>
                <Text>Barcodes</Text>
              </View>

              {/* Barcode List */}
              <View style={styles.barcodeList}>
                {barcodes.map((item, index) =>
                  Array.from({ length: item.count }).map((_, repeatIndex) => (
                    <View key={`${index}-${repeatIndex}`} style={styles.barcodeItem}>
                      <Image src={item.barcode} style={styles.barcodeImage} />
                      <Text style={styles.barcodeLabel}>{item.description}</Text>
                    </View>
                  ))
                )}
              </View>
            </Page>
          </Document>
        </PDFViewer>
      </Modal>
      <Col span={24}>
        <div className='text-right pb-2'>
          {inventories?.inventoryList?.length < 1 && (<Button onClick={createnNewInventory}>New Inventory {product.name}</Button>) }
          <Button onClick={printBarCodes}>Print Barcodes</Button>
        </div>

        {showAddInventoryForm && (
          <AddInventory closeForm={closeInventoryForm} />
        )}

        {inventories.loading.fetching}
        <Table
          rowClassName={'h-50'}
          columns={columns}
          dataSource={inventories.inventoryList}
          pagination={false}
          loading={dataFetcher.isValidating || dataFetcher.isLoading}
          scroll={{
            x: 'max-content',
            // y: 55 * 5,
          }}
        />
      </Col>
    </Row>
  );
}

export default ProductInventory